import { useEffect, useState, memo, useCallback, useMemo } from 'react'
import keyBy from 'lodash/keyBy'
import { Button, Grid, IconButton, Tooltip } from '@material-ui/core'
import { Close } from '@material-ui/icons'

import ResultsTable from '../../ResultsTable'
import Active from 'components/commons/Active'
import Inactive from 'components/commons/Inactive'
import RoleLogList from './RoleLogLIst'
import UserGroupsLogList from './GroupsLogList'

import { getFromApi } from 'actions'
import { useApiRequest } from 'utils/customHooks/useApiRequest'
import { VALUES } from 'utils/constants'
import { DIFF_TEXT_COLUMNS_PROP, differencesMapper } from './LogTable.utils'

const HIGHLIGHT_COLOR = '#FFC1071A'
let pageInitalState = VALUES.PAGING_ORDER_INITIAL_STATE
const customActions = []

const mappedData = ({ content, pageable, docTypesOptions, ...row }) => ({
   data: findAndInsertDifferences(content)?.map(user => ({
      ...user,
      id: user?.item,
      adminUser: user?.adminUser ? 'SI' : 'NO',
      userType: user?.userType == 'I' ? 'Interno' : 'Externo',
      documentType: docTypesOptions?.[user?.documentType]?.label,
      lastModifiedDate: new Date(user?.lastModifiedDate)?.toLocaleDateString() || ''
   })),
   pageSize: pageable?.pageSize,
   pageNumber: pageable?.pageNumber,
   totalRows: row?.totalElements
})

const LogUserAuditTable = ({ userId, onClose }) => {
   const [data, setData] = useState({})

   const docTypesOptions = useMemo(() => keyBy(VALUES.DOCUMENTS_TYPES, 'value'), [VALUES.DOCUMENTS_TYPES])
   const columns = [
      { title: 'ITEM', field: 'item', align: 'center' },
      { title: 'USUARIO DE MODIFICACIÓN', field: 'modifiedBy', align: 'center' },
      { title: 'FECHA DE MODIFICACIÓN', field: 'lastModifiedDate', align: 'center' },
      {
         title: 'TIPO DE IDENTIFICACIÓN',
         field: 'documentType',
         align: 'center',
         render: rowData => <ChangeCell row={rowData} field="documentType" />
      },
      {
         title: 'NÚMERO DE IDENTIFICACIÓN',
         field: 'documentNumber',
         align: 'center',
         render: rowData => <ChangeCell row={rowData} field="documentNumber" />
      },
      {
         title: 'NOMBRE',
         field: 'name',
         align: 'center',
         render: rowData => <ChangeCell row={rowData} field="name" />
      },
      {
         title: 'APELLIDOS',
         field: 'lastName',
         align: 'center',
         render: rowData => <ChangeCell row={rowData} field="lastName" />
      },
      {
         title: 'CORREO ELECTRÓNICO',
         field: 'email',
         align: 'center',
         render: rowData => <ChangeCell row={rowData} field="email" />
      },
      {
         title: 'NUMERO CELULAR',
         field: 'cellphone',
         align: 'center',
         render: rowData => <ChangeCell row={rowData} field="cellphone" />
      },
      {
         title: 'TELÉFONO',
         field: 'telephone',
         align: 'center',
         render: rowData => <ChangeCell row={rowData} field="telephone" />
      },
      {
         title: 'ENTIDAD',
         field: 'entityName',
         align: 'center',
         render: rowData => <ChangeCell row={rowData} field="entityName" />
      },
      {
         title: 'ADMINISTRADOR',
         field: 'adminUser',
         align: 'center',
         render: rowData => <ChangeCell row={rowData} field="adminUser" />
      },
      {
         title: 'TIPO DE USUARIO',
         field: 'userType',
         align: 'center',
         render: rowData => <ChangeCell row={rowData} field="userType" />
      },
      {
         title: 'ESTADO',
         field: 'status',
         align: 'center',
         render: rowData => (rowData?.status != 'I' ? <Active /> : <Inactive />)
      },
      {
         title: 'ROL',
         field: 'roles',
         align: 'center',
         render: rowData => <RoleLogList records={rowData?.roles} addedItemColor={HIGHLIGHT_COLOR} />
      },
      {
         title: 'GRUPOS',
         field: 'grupos',
         align: 'center',
         render: rowData => <UserGroupsLogList records={rowData?.grupos} addedItemColor={HIGHLIGHT_COLOR} />
      }
   ]

   const _getUserAudit = async pagingParams => {
      const { page, size } = pagingParams
      const isFirstPage = !page
      const url = (page, pageSize) => `${process.env.REACT_APP_USERS_API}/users/${userId}?page=${page}&size=${pageSize}`
      const [actualPage, prevPage] = await Promise.all([
         getFromApi(url(page, size)),
         isFirstPage ? {} : getFromApi(url(page - 1, size))
      ])

      const records = isFirstPage
         ? actualPage
         : { ...actualPage, content: [...(prevPage?.content?.slice(-1) ?? []), ...actualPage?.content] }

      try {
         setData(differencesMapper({ ...records, docTypesOptions }))
      } catch (error) {
         console.log(error)
      }
   }

   const getUserAudit = useApiRequest(_getUserAudit, 'Usuarios: Auditoría')

   const handlePaging = useCallback(async (_, values) => {
      pageInitalState = { ...pageInitalState, ...values }
      await getUserAudit(pageInitalState)
   }, [])

   useEffect(() => {
      const fetchData = async () => {
         await getUserAudit(pageInitalState)
      }
      fetchData()
      return () => {
         setData({})
         pageInitalState = VALUES.PAGING_ORDER_INITIAL_STATE
      }
   }, [])

   return (
      <>
         <Grid container justifyContent="flex-end">
            {!data?.data?.length && (
               <Button variant="outlined" color="primary" onClick={() => onClose()}>
                  Volver
               </Button>
            )}
         </Grid>
         <ResultsTable
            columns={columns}
            tableData={data}
            isSearch={true}
            searchCallback={handlePaging}
            cleanCallback={onClose}
            customActions={customActions}
            toolbarTitle="Ver Traza"
            ToolbarComponents={<CloseComponent action={onClose} />}
            order={'asc'}
         />
      </>
   )
}

const CloseComponent = ({ action }) => (
   <Tooltip title="Volver" arrow placement="left">
      <IconButton onClick={action}>
         <Close color="primary" />
      </IconButton>
   </Tooltip>
)

const ChangeCell = ({ row, field }) => {
   const highlighted = row?.[DIFF_TEXT_COLUMNS_PROP]?.includes(field)
   return <div style={{ background: highlighted ? HIGHLIGHT_COLOR : 'transparent' }}>{row?.[field]}</div>
}

export default memo(LogUserAuditTable)
